import type {
  DrawingScene,
  MembraneTakeoff,
  ValidationMessage,
  WindZoneGeometry,
  ZoneResult,
} from '../../gen/ejot/roofing/v1/types';
import type { LayerVisibility } from '../roof-plan-editor/render';
import type { DrawingDisplaySettings } from '../view/drawingDisplaySettings';
import { ActionableDiagnostic } from '../diagnostics/ActionableDiagnostic';
import { CalculatedFixingPlan } from './CalculatedFixingPlan';

interface MembraneStockSummary {
  productionWidthMm: number;
  productionLengthMm: number;
  rollCount: number;
  pieceCount: number;
  cutCount: number;
  grossStockAreaM2: number;
  installedStockAreaM2: number;
  reusableRemainderAreaM2: number;
  fireAreaM2: number;
  utilizationPercent: number;
}

/**
 * Installer-facing membrane laying-plan presentation.
 *
 * The backend-authored `membrane-layout` scene is the sole source of sheet,
 * lap, seam, fixing and stock-allocation coordinates/data. The browser only
 * presents those values; it does not run a cutting or material optimizer.
 */
export function MembraneLayingPlan({
  scene,
  zones,
  results,
  messages,
  layers,
  displaySettings,
  selectedZoneId,
  onZoneSelect,
  exportFileName,
  membraneTakeoff,
  onLayerToggle,
}: {
  scene: DrawingScene | undefined;
  zones: readonly WindZoneGeometry[];
  results: readonly ZoneResult[];
  messages: readonly ValidationMessage[];
  layers: LayerVisibility;
  displaySettings?: DrawingDisplaySettings;
  selectedZoneId?: string;
  onZoneSelect?: (zoneId: string | undefined) => void;
  exportFileName?: string;
  membraneTakeoff?: MembraneTakeoff;
  onLayerToggle?: (layerId: string, visible: boolean) => void;
}) {
  const hasAuthoritativeScene = Boolean(scene && (scene.primitives?.length ?? 0) > 0);

  if (!hasAuthoritativeScene || zones.length === 0 || results.length === 0) {
    return (
      <div className="viewer-overlay membrane-plan-readiness" role="status" aria-live="polite">
        <ActionableDiagnostic
          messages={messages}
          fallback={membranePlanFallback({ hasAuthoritativeScene, zones, results })}
        />
      </div>
    );
  }

  const stock = membraneStockSummary(scene);

  return (
    <div className="membrane-laying-plan" aria-label="Membrane laying plan">
      {stock && <MembraneStockSummaryCard summary={stock} />}
      <CalculatedFixingPlan
        scene={scene}
        zones={zones}
        results={results}
        messages={messages}
        layers={layers}
        displaySettings={displaySettings}
        selectedZoneId={selectedZoneId}
        onZoneSelect={onZoneSelect}
        exportFileName={exportFileName}
        variant="membrane"
        membraneTakeoff={membraneTakeoff}
        onLayerToggle={onLayerToggle}
      />
    </div>
  );
}

function MembraneStockSummaryCard({ summary }: { summary: MembraneStockSummary }) {
  return (
    <div className="engineering-table-scroll membrane-stock-summary">
      <table className="engineering-table" aria-label="Optimized membrane production stock summary">
        <thead>
          <tr>
            <th scope="col">Production roll</th>
            <th scope="col" className="numeric">Rolls</th>
            <th scope="col" className="numeric">Installed stock</th>
            <th scope="col" className="numeric">Reusable remainder</th>
            <th scope="col" className="numeric">Material fire / waste</th>
            <th scope="col" className="numeric">Utilization</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{millimetres(summary.productionWidthMm)} × {millimetres(summary.productionLengthMm)}</td>
            <td className="numeric">{summary.rollCount}</td>
            <td className="numeric">{squareMetres(summary.installedStockAreaM2)}</td>
            <td className="numeric">{squareMetres(summary.reusableRemainderAreaM2)}</td>
            <td className="numeric">{squareMetres(summary.fireAreaM2)}</td>
            <td className="numeric">{summary.utilizationPercent.toFixed(1)}%</td>
          </tr>
        </tbody>
      </table>
      <p className="field-note">
        The numbers on the drawing identify the installed membranes for reading the plan; they are not source-roll numbers and they do not give the installation order. The membrane is laid perimeter first, and the backend cutting allocation assigns each piece its own production roll, which one roll may supply for several separately installed membranes.
      </p>
    </div>
  );
}

function membraneStockSummary(scene: DrawingScene | undefined): MembraneStockSummary | undefined {
  const encoded = scene?.primitives?.find((primitive) => primitive.id === 'membrane-stock-summary')?.text;
  if (!encoded) return undefined;
  try {
    const parsed = JSON.parse(encoded) as Partial<MembraneStockSummary>;
    const required = [
      parsed.productionWidthMm,
      parsed.productionLengthMm,
      parsed.rollCount,
      parsed.installedStockAreaM2,
      parsed.reusableRemainderAreaM2,
      parsed.fireAreaM2,
      parsed.utilizationPercent,
    ];
    if (required.some((value) => typeof value !== 'number' || !Number.isFinite(value))) return undefined;
    return parsed as MembraneStockSummary;
  } catch {
    return undefined;
  }
}

function millimetres(value: number): string {
  return `${Math.round(value).toLocaleString('en-GB')} mm`;
}

function squareMetres(value: number): string {
  return `${value.toFixed(2)} m²`;
}

function membranePlanFallback({
  hasAuthoritativeScene,
  zones,
  results,
}: {
  hasAuthoritativeScene: boolean;
  zones: readonly WindZoneGeometry[];
  results: readonly ZoneResult[];
}): string {
  if (zones.length === 0) {
    return 'Complete the roof geometry and wind inputs to produce the membrane plan. Sheets, laps, seams and fixing coordinates are drawn from the calculated result, so none of them can be shown until the roof has wind zones.';
  }
  if (results.length === 0) {
    return 'Select the product and resistance basis to produce the membrane plan. Sheets, laps, seams and fixing coordinates are drawn from the calculated result, so none of them can be shown until the fixing calculation has run.';
  }
  if (!hasAuthoritativeScene) {
    return 'Check the membrane course inputs — roll width, overlap, roll length, end lap and stagger — together with any messages, then the laying plan will be drawn. The calculation returned no membrane layout for these values, and the plan is only ever drawn from what it returns.';
  }
  return 'The membrane plan appears once the active roof produces a membrane layout.';
}
